/**
 * Registro de produtos (verticais) da plataforma QC — lado SPA.
 *
 * F1 da fusão (ROADMAP-FUSAO.md): o core não referencia marca de vertical;
 * a identidade sai deste registro. O produto ativo é fixo em 'sst' até a F3
 * (branding por domínio), quando passa a ser resolvido pelo host.
 * Espelho parcial no backend: app/api/platform.ts (manter em sincronia).
 */

type ProductId = 'sst' | 'qualidade'

type ProductSeo = {
  home_description: string
  login_description: string
  privacy_description: string
  onboarding_description: string
  private_description: string
}

type ProductDefinition = {
  id: ProductId
  /** Marca curta exibida no shell e em títulos. */
  brand: string
  /** Sigla do avatar quadrado (login/shell). */
  sigla: string
  /** Tagline curta sob a marca no sidebar. */
  tagline: string
  /** Nome completo usado em <title> e og:site_name. */
  brand_full: string
  /** Subtítulo do cabeçalho da tela de login. */
  login_subtitle: string
  /** Copy do passo de boas-vindas do onboarding (fallback local). */
  onboarding_intro: string
  /** Nota da fase soft (P0) do wizard — exemplos de cadastros do vertical. */
  onboarding_soft_note: string
  /** Banner de ativação no dashboard. */
  pitch_banner: string
  /** Subtítulo das telas de primeiros passos. */
  pitch_subtitle: string
  /** Convidado em `/` vê landing pública? (senão vai pro login) */
  landing_guest: boolean
  /** Páginas públicas indexáveis? (domínio de app do SGQ é noindex) */
  indexable: boolean
  seo: ProductSeo
}

const PRODUCTS: Record<ProductId, ProductDefinition> = {
  sst: {
    id: 'sst',
    brand: 'NR-01',
    sigla: 'NR',
    tagline: 'GRO · PGR',
    brand_full: 'NR-01 — GRO e PGR',
    login_subtitle: 'NR-01 / GRO / PGR',
    onboarding_intro:
      'Em poucos passos você registra a empresa, a estrutura, um risco, uma ação e uma evidência — a base da NR-01 no dia a dia.',
    onboarding_soft_note:
      'Nesta etapa inicial o progresso é registrado no wizard. A gravação completa nos cadastros (empresa, risco, ação…) chega na próxima fase.',
    pitch_banner: 'Complete a ativação mínima da organização para operar o PGR com evidências.',
    pitch_subtitle: 'Configure o mínimo para operar o PGR com evidências.',
    landing_guest: true,
    indexable: true,
    seo: {
      home_description:
        'Plataforma NR-01 para Gestão de Riscos Ocupacionais, PGR e conformidade regulatória — trial self-serve com Google, cadeia M02–M28, auditoria e multi-tenancy.',
      login_description:
        'Acesse o NR-01 com Google ou e-mail. Trial self-serve com provisionamento imediato de organização.',
      privacy_description: 'Consentimentos e solicitações do titular (DSR) com trilha de auditoria no NR-01.',
      onboarding_description: 'Configure o mínimo para operar o PGR com evidências no NR-01.',
      private_description: 'Área autenticada do NR-01 — Gestão de Riscos Ocupacionais e conformidade regulatória.'
    }
  },
  qualidade: {
    id: 'qualidade',
    brand: 'SGQ',
    sigla: 'SQ',
    tagline: 'Qualidade · Conformidade',
    brand_full: 'SGQ — Plataforma de Conformidade Inteligente',
    login_subtitle: 'Gestão da Qualidade e Conformidade',
    onboarding_intro:
      'Em poucos passos você registra a empresa, os documentos e as primeiras evidências — a base do seu sistema de gestão da qualidade.',
    onboarding_soft_note:
      'Nesta etapa inicial o progresso é registrado no wizard. A gravação completa nos cadastros (empresa, normas, documento, não conformidade…) chega na próxima fase.',
    pitch_banner: 'Complete a ativação mínima da organização para operar a gestão da qualidade com evidências.',
    pitch_subtitle: 'Configure o mínimo para operar a gestão da qualidade com evidências.',
    landing_guest: false,
    indexable: false,
    seo: {
      home_description:
        'SGQ — plataforma de gestão da qualidade e conformidade: documentos, auditorias, não conformidades, CAPA e painel de conformidade.',
      login_description: 'Acesse o SGQ com o seu e-mail corporativo.',
      privacy_description: 'Consentimentos e solicitações do titular (DSR) com trilha de auditoria no SGQ.',
      onboarding_description: 'Configure o mínimo para operar a gestão da qualidade com evidências no SGQ.',
      private_description: 'Área autenticada do SGQ — Gestão da Qualidade e Conformidade.'
    }
  }
}

/** Resolução por host (F3): domínio do SGQ → vertical qualidade; default sst. */
function product_id_for_hostname(hostname: string): ProductId {
  const clean = String(hostname || '').toLowerCase()
  if (clean === 'isgq.com.br' || clean.endsWith('.isgq.com.br')) return 'qualidade'
  return 'sst'
}

const ACTIVE_PRODUCT_ID: ProductId =
  typeof location === 'undefined' ? 'sst' : product_id_for_hostname(location.hostname)

/** Produto ativo, resolvido pelo host da página. */
export const PRODUCT: ProductDefinition = PRODUCTS[ACTIVE_PRODUCT_ID]

/**
 * Apps-mãe (sistemas principais) da plataforma QC.
 * Troca de “sistema” = estes dois, nunca módulos M0x nem bibliotecas.
 */
export type MotherSystem = {
  id: ProductId
  brand: string
  /** Nome ultra-curto no chip do topbar. */
  chip: string
  tagline: string
  /** URL pública do vertical (aceite). */
  url: string
}

export const MOTHER_SYSTEMS: readonly MotherSystem[] = [
  {
    id: 'sst',
    brand: 'NR-01',
    chip: 'NR-01',
    tagline: 'GRO · PGR',
    url: 'https://nr01.tec.br'
  },
  {
    id: 'qualidade',
    brand: 'SGQ',
    chip: 'SGQ',
    tagline: 'Qualidade · Conformidade',
    url: 'https://adm.isgq.com.br'
  }
] as const
